import { useEffect, useState } from "react";
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";

import VuiBox from "components/VuiBox";
import VuiButton from "components/VuiButton";
import VuiTypography from "components/VuiTypography";
import VuiAutocomplete from "components/AutoComplete";

import { FaExclamationCircle, FaTimes } from "react-icons/fa";

import useCommon from "hooks/useCommon";

import {
  assignHousekeepingRoom,
  getHousekeepers,
  removeHousekeepingRoomAssignment,
  resetAssignRoomState,
  resetRemoveAssignmentState,
} from "store/slice/housekeepingSlice";

const getUserId = (user) => user?.userId || user?._id || user?.id || "";

const getUserName = (user) => user?.fullName || user?.name || user?.email || "";

const AssignRoomDialog = ({ open, room, onClose }) => {
  const { DISPATCH, STORE } = useCommon();

  const hotelIds = STORE((state) => state.ui.hotelIds);

  const hotelId = hotelIds?.[0];

  const {
    housekeepers,
    housekeepersLoading,
    housekeepersError,

    assignRoomLoading,
    assignRoomError,

    removeAssignmentLoading,
    removeAssignmentError,
  } = STORE((state) => state.housekeeping);

  const [selectedUserId, setSelectedUserId] = useState("");

  const assignedUserId =
    room?.assignedUserId ||
    room?.assignedStaff?.userId ||
    room?.assignedStaff?._id ||
    room?.assignedStaff?.id ||
    room?.assignedTo?._id ||
    room?.assignedTo?.id ||
    room?.assignedToUserId ||
    "";

  const selectedUser = housekeepers?.find((user) => getUserId(user) === selectedUserId) || null;

  const assignmentUnchanged = Boolean(selectedUserId) && selectedUserId === assignedUserId;

  const submitting = assignRoomLoading || removeAssignmentLoading;

  useEffect(() => {
    if (!open || !hotelId) {
      return;
    }

    DISPATCH(resetAssignRoomState());
    DISPATCH(resetRemoveAssignmentState());

    DISPATCH(getHousekeepers(hotelId));
  }, [open, hotelId, DISPATCH]);

  useEffect(() => {
    if (!open) return;

    setSelectedUserId(assignedUserId || "");
  }, [open, room, assignedUserId]);

  useEffect(() => {
    if (open) return;

    setSelectedUserId("");

    DISPATCH(resetAssignRoomState());
    DISPATCH(resetRemoveAssignmentState());
  }, [open, DISPATCH]);

  const handleClose = () => {
    if (submitting || housekeepersLoading) {
      return;
    }

    setSelectedUserId("");

    DISPATCH(resetAssignRoomState());
    DISPATCH(resetRemoveAssignmentState());

    onClose?.();
  };

  const handleAssign = async () => {
    if (
      !hotelId ||
      !room?.id ||
      !selectedUserId ||
      submitting ||
      housekeepersLoading ||
      assignmentUnchanged ||
      room?.version === undefined ||
      room?.version === null
    ) {
      return;
    }

    try {
      await DISPATCH(
        assignHousekeepingRoom({
          roomId: room.id,
          hotelId,
          assigneeUserId: selectedUserId,
          version: room.version,
        })
      ).unwrap();

      setSelectedUserId("");

      DISPATCH(resetAssignRoomState());

      onClose?.();
    } catch (error) {
      // Error is stored in Redux.
      // Keep the dialog open so the user can see it.
    }
  };

  const handleRemoveAssignment = async () => {
    if (
      !hotelId ||
      !room?.id ||
      !assignedUserId ||
      submitting ||
      housekeepersLoading ||
      room?.version === undefined ||
      room?.version === null
    ) {
      return;
    }

    try {
      await DISPATCH(
        removeHousekeepingRoomAssignment({
          roomId: room.id,
          hotelId,
          version: room.version,
        })
      ).unwrap();

      setSelectedUserId("");

      DISPATCH(resetRemoveAssignmentState());

      onClose?.();
    } catch (error) {
      // Error is stored in Redux.
      // Keep the dialog open so the user can see it.
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || submitting || housekeepersLoading) {
          return;
        }

        handleClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        <VuiBox display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          <VuiBox>
            <VuiTypography variant="lg">
              {assignedUserId ? "Reassign Room" : "Assign Room"}
            </VuiTypography>

            <VuiTypography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.4,
                opacity: 0.65,
              }}
            >
              {room?.roomName || room?.roomNumber || "Selected room"}
            </VuiTypography>
          </VuiBox>

          <VuiButton
            size="small"
            variant="text"
            color="primary"
            onClick={handleClose}
            disabled={submitting || housekeepersLoading}
            sx={{
              minWidth: 34,
              width: 34,
              height: 34,
              px: 0,
              borderRadius: "10px",
            }}
          >
            <FaTimes size={13} />
          </VuiButton>
        </VuiBox>
      </DialogTitle>

      <DialogContent>
        {housekeepersLoading ? (
          <VuiBox
            py={4}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={1.5}
          >
            <CircularProgress size={28} />

            <VuiTypography
              variant="caption"
              sx={{
                opacity: 0.65,
              }}
            >
              Loading housekeepers...
            </VuiTypography>
          </VuiBox>
        ) : (
          <VuiBox mt={0.5}>
            <VuiTypography variant="caption" ml={1}>
              Assign To*
            </VuiTypography>

            <VuiAutocomplete
              options={housekeepers || []}
              value={housekeepers?.find((user) => getUserId(user) === selectedUserId) || null}
              getOptionLabel={(option) =>
                `${getUserName(option)} (${option?.assignedCount || 0} rooms assigned)`
              }
              isOptionEqualToValue={(option, value) => getUserId(option) === getUserId(value)}
              placeholder="Select housekeeper"
              gradient
              disabled={submitting}
              onChange={(_, selected) => {
                setSelectedUserId(selected ? getUserId(selected) : "");

                DISPATCH(resetAssignRoomState());

                DISPATCH(resetRemoveAssignmentState());
              }}
            />

            {housekeepersError && (
              <VuiBox
                display="flex"
                alignItems="center"
                gap={1}
                p={1.2}
                mt={2}
                borderRadius="md"
                sx={{
                  border: "1px solid rgba(239,68,68,0.4)",
                  background: "rgba(239,68,68,0.08)",
                }}
              >
                <FaExclamationCircle size={14} color="#ef4444" />

                <VuiTypography variant="caption" color="error">
                  {typeof housekeepersError === "string"
                    ? housekeepersError
                    : housekeepersError?.message || "Failed to fetch housekeepers"}
                </VuiTypography>
              </VuiBox>
            )}

            {assignRoomError && (
              <VuiBox
                display="flex"
                alignItems="center"
                gap={1}
                p={1.2}
                mt={2}
                borderRadius="md"
                sx={{
                  border: "1px solid rgba(239,68,68,0.4)",
                  background: "rgba(239,68,68,0.08)",
                }}
              >
                <FaExclamationCircle size={14} color="#ef4444" />

                <VuiTypography variant="caption" color="error">
                  {typeof assignRoomError === "string"
                    ? assignRoomError
                    : assignRoomError?.message || "Failed to assign the room"}
                </VuiTypography>
              </VuiBox>
            )}

            {removeAssignmentError && (
              <VuiBox
                display="flex"
                alignItems="center"
                gap={1}
                p={1.2}
                mt={2}
                borderRadius="md"
                sx={{
                  border: "1px solid rgba(239,68,68,0.4)",
                  background: "rgba(239,68,68,0.08)",
                }}
              >
                <FaExclamationCircle size={14} color="#ef4444" />

                <VuiTypography variant="caption" color="error">
                  {typeof removeAssignmentError === "string"
                    ? removeAssignmentError
                    : removeAssignmentError?.message || "Failed to remove room assignment"}
                </VuiTypography>
              </VuiBox>
            )}
          </VuiBox>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        {assignedUserId && (
          <VuiButton
            variant="outlined"
            color="error"
            onClick={handleRemoveAssignment}
            disabled={submitting || housekeepersLoading}
            sx={{
              mr: "auto",
            }}
          >
            {removeAssignmentLoading ? "Removing..." : "Remove Assignment"}
          </VuiButton>
        )}

        <VuiButton
          variant="outlined"
          color="primary"
          onClick={handleClose}
          disabled={submitting || housekeepersLoading}
        >
          Cancel
        </VuiButton>

        <VuiButton
          variant="contained"
          color="primary"
          onClick={handleAssign}
          disabled={!selectedUserId || submitting || housekeepersLoading || assignmentUnchanged}
        >
          {assignRoomLoading
            ? assignedUserId
              ? "Reassigning..."
              : "Assigning..."
            : assignedUserId
              ? "Reassign"
              : "Assign"}
        </VuiButton>
      </DialogActions>
    </Dialog>
  );
};

export default AssignRoomDialog;
